/**
 * Calculator Slider — pure helpers.
 *
 * The calculator engine (CalculatorContainer in container.js) wires up
 * the input event listeners and coordinates re-calculation across all
 * fields, so it owns the orchestration. What lives here are the
 * stateless render helpers — fill the track gradient, render the
 * formatted value next to the label, and optional label tooltips — that
 * the controller calls on init and on every input event.
 */

import { formatNumber } from '../../../../container/clientlibs/site/js/format.js';

/**
 * Set the slider's fill gradient based on its current value relative
 * to its min/max range. Surfaces the 0–100% to CSS via `--fill`.
 */
export function updateSliderFill(slider) {
    const min = parseFloat(slider.min) || 0;
    const max = parseFloat(slider.max) || 100;
    const val = parseFloat(slider.value) || 0;
    const pct = ((val - min) / (max - min)) * 100;
    slider.style.setProperty('--fill', `${pct}%`);
}

/**
 * Render the slider's formatted current value into the
 * `.calc-slider__number` span. Updates only the number — the
 * surrounding `__prefix` / `__suffix` elements are driven by the
 * unit-toggle (`[data-template]` resolution) and would otherwise be
 * destroyed by overwriting the parent's textContent.
 *
 * Also keeps `aria-valuetext` on the range in sync with the visible,
 * formatted value (prefix + number + suffix) so screen readers
 * announce "$25 thousand" / "50 %" instead of the raw `valuenow`
 * number. The visual `__value-display` is `aria-hidden` precisely
 * because this is the source of truth for AT.
 */
export function updateSliderDisplay(slider, locale) {
    const container = slider.closest('.calc-slider');
    if (!container) return;
    const numberEl = container.querySelector('.calc-slider__number');
    if (!numberEl) return;

    const formatKey = container.getAttribute('data-format') || 'integer';
    const val = parseFloat(slider.value) || 0;
    const formatted = formatNumber(val, formatKey, locale);
    numberEl.textContent = formatted;

    const prefixEl = container.querySelector('.calc-slider__prefix');
    const suffixEl = container.querySelector('.calc-slider__suffix');
    const prefix = prefixEl ? prefixEl.textContent : '';
    const suffix = suffixEl ? suffixEl.textContent : '';
    slider.setAttribute('aria-valuetext', `${prefix}${formatted}${suffix}`.trim());
}

/**
 * Initialize every slider's fill + value-display on first render.
 */
export function initSliders(sliders, locale) {
    sliders.forEach((slider) => {
        updateSliderFill(slider);
        updateSliderDisplay(slider, locale);
    });
}

/** Help bubble beside the label: CSS-only above-icon placement; no viewport flip. */
export function initSliderTooltips(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    root.querySelectorAll('.calc-slider__tooltip-container').forEach((container) => {
        const tooltip = container.querySelector('.calc-slider__tooltip');
        if (!tooltip) return;

        const show = () => {
            tooltip.style.opacity = '1';
            tooltip.style.visibility = 'visible';
        };
        const hide = () => {
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
        };

        container.addEventListener('mouseenter', show);
        container.addEventListener('mouseleave', hide);
        container.addEventListener('focusin', show);
        container.addEventListener('focusout', (e) => {
            if (!container.contains(e.relatedTarget)) {
                hide();
            }
        });
    });
}
